import React from 'react';
import { CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { eventsData, categoryColors } from '../../../data/eventsData';

const getStatusStyle = (status: string): { style: string; icon: React.ReactNode } => {
  const styles: Record<string, { style: string; icon: React.ReactNode }> = {
    'ON TRACK': { style: 'bg-data-green text-black font-bold', icon: <CheckCircle size={10} /> },
    'IN PROGRESS': { style: 'bg-data-amber text-black font-semibold', icon: <Clock size={10} /> },
    'DELAYED': { style: 'bg-data-red text-white font-semibold', icon: <AlertCircle size={10} /> },
  };
  return styles[status] || { style: 'bg-gray-600 text-white', icon: null };
};

interface ReformTrackerPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const ReformTrackerPanel: React.FC<ReformTrackerPanelProps> = ({ isMinimized, onMinimize }) => {
  const { reformTracker, summary } = eventsData;

  const avgProgress = reformTracker.reduce((sum, r) => sum + r.progress, 0) / reformTracker.length;

  return (
    <Panel title="REFORM TRACKER" subtitle="PROGRESS" status="static" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full p-3">
        {/* Hero Stats Row */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="bg-terminal-bg/60 rounded-lg p-2.5 text-center border border-terminal-border/50">
            <div className="text-xl font-mono font-bold text-white">{reformTracker.length}</div>
            <div className="text-xs text-gray-500">Reforms</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-2.5 text-center border border-data-green/30">
            <div className="text-xl font-mono font-bold text-data-green">{summary.reformsOnTrack}</div>
            <div className="text-xs text-gray-500">On Track</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-2.5 text-center border border-data-amber/30">
            <div className="text-xl font-mono font-bold text-data-amber">{summary.reformsInProgress}</div>
            <div className="text-xs text-gray-500">In Progress</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-2.5 text-center border border-terminal-border/50">
            <div className="text-xl font-mono font-bold text-white">{avgProgress.toFixed(0)}%</div>
            <div className="text-xs text-gray-500">Avg Progress</div>
          </div>
        </div>

        {/* Reform Cards */}
        <div className="flex-1 overflow-auto custom-scrollbar space-y-2">
          {reformTracker.map((reform, idx) => {
            const statusInfo = getStatusStyle(reform.status);
            const completedMilestones = reform.milestones.filter(m => m.done).length;
            return (
              <div
                key={idx}
                className={`bg-terminal-bg/40 rounded-lg p-3 border ${
                  reform.status === 'ON TRACK' ? 'border-data-green/30' :
                  reform.status === 'IN PROGRESS' ? 'border-data-amber/30' : 'border-terminal-border/30'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-white">{reform.reform}</span>
                  <span className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${statusInfo.style}`}>
                    {statusInfo.icon}
                    {reform.status}
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="h-2 bg-terminal-border rounded-full overflow-hidden mb-2">
                  <div
                    className={`h-full rounded-full transition-all ${
                      reform.status === 'ON TRACK' ? 'bg-data-green' :
                      reform.status === 'IN PROGRESS' ? 'bg-data-amber' : 'bg-data-red'
                    }`}
                    style={{ width: `${reform.progress}%` }}
                  />
                </div>

                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span style={{ color: categoryColors[reform.category] }} className="text-xs">
                      {reform.category}
                    </span>
                    <span className="text-gray-500 text-xs">Target: {reform.targetDate}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                      <span className="text-data-green font-bold">{completedMilestones}</span>/{reform.milestones.length} milestones
                    </span>
                    <span className="text-lg font-mono font-black text-white">{reform.progress}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.STAT_UZ} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default ReformTrackerPanel;
